// Component: StrategyAnalysisView.jsx
// Purpose: The center pane of Strategy Builder Step 3 — SmartChart on top,
//          RDWatchDashboard below, with an expand/collapse animation between
//          them. Extracted so the Algo Dashboard's Live Analysis tab can
//          render the same experience against a deployed strategy without
//          duplicating ~300 lines of derivation.
//
//          Pass mode="rd" + raw nodes/edges + instrument. Internally handles
//          visual-layer filtering, strategy-indicator projection, categorized
//          node grouping, and all the callback wiring SmartChart↔RDWatchDashboard
//          needs. Caller only provides the strategy graph + live/replay context.

import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { FiMaximize2, FiMinimize2 } from 'react-icons/fi';

import SmartChart from '../../../../../components/SmartChart/SmartChart';
import RDWatchDashboard from './RDWatchDashboard';
import { resolveSmartLabel, categorizeNodes, deriveLiveNodeOutputs, deriveLiveNodeInvalidReasons, keepIfSameReasons } from "../utils/nodeUtils";
import { deriveVisualLayers, deriveStrategyIndicators } from "../utils/chartDataDerivation";
import { services } from '../../../../../services/api';
import { useBreakpoint } from '../../../../../hooks/useBreakpoint';

//* 50-12 R6 — the ONE collapsed-chart-card size table, keyed by the app's tier names (hooks/useBreakpoint.js).
//*   Compact tiers use viewport-relative dvh, set directly on style.height (framer-motion has no dvh value
//*   type, so it cannot tween it; critic F6: no custom property); desktop/wide keep the released 50-11 rule
//*   (55% of the column, 360px floor) through the existing Framer tween.
const COLLAPSED_CHART_SIZE = {
  phone: { height: '60dvh', minHeight: 320 },
  tablet: { height: '55dvh', minHeight: 360 },
  desktop: { height: '55%', minHeight: 360 },
  wide: { height: '55%', minHeight: 360 },
};
//* 50-12: compact dashboard floor = 4 x 44px accordion headers + 64px visible body; the analysis host scrolls.
const COMPACT_DASHBOARD_MIN_HEIGHT = 240;

const StrategyAnalysisView = ({
  //* 50-15 — registry category map from LiveAnalysisTab, forwarded to the dashboard (undefined → its {} default).
  categoryByIndicator,
  nodes = [],
  edges = [],
  //* 50-6 D35 — defaults to `nodes` so Builder (and any caller that never computes an
  //* override map) is untouched: computation and display stay the SAME value, exactly
  //* today's behavior (R7.3). Live Analysis passes RAW strategyConfig.nodes here while
  //* `nodes` carries the merged EFFECTIVE nodes for display only.
  computationNodes = nodes,
  instrument,
  liveTick,
  user,
  levelHistory = null,
  //* 50-17 — open-trade SL/TP by side for the dashboard rows (Live Analysis only; undefined elsewhere)
  positionLevels = undefined,
  levelDisplayNodes = null,
  tradeMarkers = [],
  replayMode = null,
  replayRunId = null,
  backtestResults = null,
  backtestConfig = null,
  instruments,
  general,
  dateRange,
  rolloverDays = 0,
  //! LB-8 (F-06) — structural companion to rolloverDays, forwarded straight to
  //! SmartChart so resolveRolloverDays() can gate on it structurally.
  isContinuous,
  strategyId = 'live',
  //! 50-21 R10-T4 — the visual-overrides record (node id -> entry) the parent already loaded; the
  //! dashboard resolves condition names from it. Never fetched here.
  visualOverrides = undefined,
  mode = 'rd',
  fetchDateRange = null,
  onPeriodSelect,
  onBacktestRequired,
  liveNodeResults = null,
  lastEvalTime = null,
  liveBarTimeframe = null,
  //! Board #73 — engine's real post-gate verdict, kept SEPARATE from
  //*  liveNodeResults. Rides straight through to RDWatchDashboard as its own
  //*  prop (:249-269 below) — it must NOT be merged into the executionData
  //*  flatten immediately below (CF-2 hazard: that flatten has no lane
  //*  provenance and folding the decision in would inherit its ambiguity).
  engineDecision = null,
  tickType = null,
  deploymentId = null,
  resolvedInstrumentId = null,
  onGapRepair = null,
  //! 50-6 D53 visual-gate fix-up — OPTIONAL controlled selection so Live Analysis can share
  //! ONE selectedNodeId with the Indicators tab list (RDWatchDashboard already supports
  //! onItemClick/selectedNodeId natively — RDWatchDashboard.jsx:260-344 — this just lets the
  //! caller own the state instead of StrategyAnalysisView owning it internally). Builder/preview
  //! callers never pass these — internal state is UNCHANGED (undefined → self-managed, R7.3-style
  //! zero-diff for every existing caller).
  selectedNodeId: externalSelectedNodeId = undefined,
  onItemClick: externalOnItemClick = undefined,
  //* 50-14 — Live Analysis panel Dashboard tab: a DOM node (owned by LiveAnalysisTab) that receives a SECOND
  //* RDWatchDashboard instance through a portal, fed by the SAME `dashboardProps` object as the below-chart
  //* instance (one derivation, two render sites). null/undefined → no panel instance (Builder + default callers).
  panelDashboardHost = null,
  //* 50-14 — select-only click handler for the panel instance (panel stays on its tab, R8); falls back to the
  //* below-chart handler when absent.
  onPanelItemClick = undefined,
}) => {
  //--> Fetch canonical indicator metadata (backend-driven protocol).
  const [allIndicatorMetadata, setAllIndicatorMetadata] = useState([]);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await services.indicators.list();
        if (active && res.data?.status === 'success') {
          setAllIndicatorMetadata(res.data.indicators || []);
        }
      } catch (err) {
        console.error('[StrategyAnalysisView] indicator metadata load failed', err);
      }
    })();
    return () => { active = false; };
  }, []);

  //--> Responsive tier (50-12) — the one breakpoint authority; SmartChart / RDWatchDashboard stay tier-ignorant
  const { bp, isPhone, isCompact } = useBreakpoint();
  const collapsedSize = COLLAPSED_CHART_SIZE[bp] || COLLAPSED_CHART_SIZE.desktop;

  //--> Layout state
  const [isChartExpanded, setIsChartExpanded] = useState(false);
  const [isMetricsExpanded, setIsMetricsExpanded] = useState(false);

  //--> Derive primary timeframe from the strategy's data_source node (matches
  //    StrategyBuilder.jsx:1362-1365). Seed activeTimeframe from the derivation
  //    and re-sync when `nodes` changes (switching deployments or version
  //    updates). User-driven toolbar changes via onTimeframeChange still work
  //    because activeTimeframe is stateful, not a bare useMemo.
  const derivedPrimaryTf = useMemo(
    () => (
      (Array.isArray(nodes) ? nodes.find((n) => n?.type === 'data_source')?.data?.timeframe : null)
      || '5m'
    ),
    [nodes],
  );
  const [activeTimeframe, setActiveTimeframe] = useState(derivedPrimaryTf);
  useEffect(() => { setActiveTimeframe(derivedPrimaryTf); }, [derivedPrimaryTf]);
  const [activeChartType, setActiveChartType] = useState('candle');

  //--> Chart↔dashboard state
  const [latestData, setLatestData] = useState(null);
  const [hoverData, setHoverData] = useState(null);

  // CHANGE 1A: Simplified executionData — no hover override, just flat mapping.
  // The dashboard's merge (RDWatchDashboard.jsx:419-426) already handles hoverData last.
  // Pre-merging hoverData into executionData was dropping signal/position_trigger nodes.
  const executionData = useMemo(() => {
    if (!liveNodeResults) return {};
    const flat = {};
    for (const [nodeId, result] of Object.entries(liveNodeResults)) {
      flat[nodeId] = (result && typeof result === 'object' && 'value' in result)
        ? result.value
        : result;
    }
    return flat;
  }, [liveNodeResults]);

  //--> 142-37 loop 2: full outputs of reduced multi-output nodes (executionData stays scalar)
  const liveNodeOutputs = useMemo(() => deriveLiveNodeOutputs(liveNodeResults), [liveNodeResults]);
  //* 50-17: the wire's invalid_reason per node, so the condition tree shows a withheld input as unknown.
  const invalidReasonsRef = useRef({});
  const liveNodeInvalidReasons = useMemo(() => {
    invalidReasonsRef.current = keepIfSameReasons(invalidReasonsRef.current, deriveLiveNodeInvalidReasons(liveNodeResults));
    return invalidReasonsRef.current;
  }, [liveNodeResults]);

  const [indicatorMaps, setIndicatorMaps] = useState({});
  const [internalSelectedNodeId, setInternalSelectedNodeId] = useState(null);
  //! Controlled-if-provided (D53 fix-up) — external state wins so LiveAnalysisTab's lifted
  //! selectedNodeId drives this view AND the Indicators tab list from one source.
  const selectedNodeId = externalSelectedNodeId !== undefined ? externalSelectedNodeId : internalSelectedNodeId;

  //--> Derived graph projections
  const visualLayers = useMemo(() => deriveVisualLayers(nodes, edges), [nodes, edges]);
  //! One authority for the picked chart type: keep the graph's layer of that type, else re-type the graph's
  //! primary main-series layer (a candle-only graph must still draw under Line/Area/Heikin, never nothing).
  const mainSeriesTypes = ['candle', 'heikin', 'hollow_candle', 'line', 'area'];
  const primaryMainLayer = useMemo(
    () => (visualLayers || []).find((l) => mainSeriesTypes.includes(l.type) && !l.styleOptions?.is_overlay),
    [visualLayers], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const memoizedLayers = useMemo(() => {
    const hasActive = (visualLayers || []).some((l) => l.type === activeChartType);
    return (visualLayers || []).flatMap((l) => {
      if (l.styleOptions?.is_overlay || l.type === 'marker' || l.type === 'custom') return [l];
      if (l.type === 'volume') return [l];
      if (mainSeriesTypes.includes(l.type)) {
        if (l.type === activeChartType) return [l];
        if (!hasActive && l === primaryMainLayer) return [{ ...l, type: activeChartType }];
        return [];
      }
      return [l];
    });
  }, [visualLayers, activeChartType, primaryMainLayer]); // eslint-disable-line react-hooks/exhaustive-deps
  const strategyIndicators = useMemo(
    () => deriveStrategyIndicators(nodes, edges, allIndicatorMetadata, resolveSmartLabel),
    [nodes, edges, allIndicatorMetadata],
  );
  const categorizedNodes = useMemo(() => categorizeNodes(nodes, edges), [nodes, edges]);
  const activeChartNodeId = useMemo(() => (
    (visualLayers.find((l) => l.type === activeChartType) || primaryMainLayer)?.id
  ), [visualLayers, activeChartType, primaryMainLayer]);

  //--> Callbacks
  const handleDataLoaded = useCallback((sample) => {
    if (sample && Object.keys(sample).length > 0) setLatestData(sample);
  }, []);

  //! handleCrosshairMove — clear hoverData on mouse-leave so the dashboard
  //!  reverts to latest live values; otherwise show hover-time values. We do
  //!  NOT mirror StrategyBuilder's "sticky on leave" behavior because in live
  //!  mode new bars are continuously appending and the user expects the
  //!  dashboard to reflect them when they're not actively inspecting a candle.
  //* Perf (Deepak 10-24): the crosshair fires per mouse event; a state update per event re-rendered the
  //* whole node dashboard hundreds of times a second while dragging. Keep the latest payload in a ref
  //* and commit it ONCE per animation frame; a leave (null) commits immediately and drops the pending one.
  const hoverFrameRef = useRef(null);
  const hoverPendingRef = useRef(null);
  const handleCrosshairMove = useCallback((payload) => {
    if (!payload || !payload.time) {
      hoverPendingRef.current = null;
      if (hoverFrameRef.current != null) { cancelAnimationFrame(hoverFrameRef.current); hoverFrameRef.current = null; }
      setHoverData(null);
      return;
    }
    hoverPendingRef.current = payload;
    if (hoverFrameRef.current != null) return;
    hoverFrameRef.current = requestAnimationFrame(() => {
      hoverFrameRef.current = null;
      if (hoverPendingRef.current) setHoverData(hoverPendingRef.current);
    });
  }, []);
  useEffect(() => () => { if (hoverFrameRef.current != null) cancelAnimationFrame(hoverFrameRef.current); }, []);

  const handleToggleChartExpanded = useCallback(() => {
    setIsMetricsExpanded(false);
    setIsChartExpanded((v) => !v);
  }, []);
  const handleToggleMetricsExpanded = useCallback(() => {
    setIsChartExpanded(false);
    setIsMetricsExpanded((v) => !v);
  }, []);
  const handleDashboardItemClick = useCallback((node) => {
    if (externalOnItemClick) {
      externalOnItemClick(node);
      return;
    }
    setInternalSelectedNodeId(node?.id || null);
  }, [externalOnItemClick]);
  const handlePopOut = useCallback(() => { /* intentionally no-op in live view */ }, []);

  //--> 50-14 — the ONE data-props object both dashboard render sites spread (no useMemo: RDWatchDashboard's memo
  //    compares each prop separately). Site-only props (onPopOut, onItemClick, isMetricsExpanded,
  //    onToggleMetricsExpanded, responsiveLayout / panelMode) stay on each element.
  const dashboardProps = {
    categoryByIndicator,
    categorizedNodes,
    latestData,
    executionData,
    liveNodeOutputs,
    liveNodeInvalidReasons,
    allIndicatorMetadata, //* 142-37: output_labels authority for the model tile rows
    engineDecision,
    hoverData,
    selectedNodeId,
    activeChartNodeId,
    primaryTimeframe: activeTimeframe,
    nodes,
    edges,
    visualOverrides,
    indicatorMaps,
    backtestResults,
    backtestConfig,
    onPeriodSelect,
    mode,
    positionLevels,
  };

  return (
    // 50-11 follow-up (Deepak): no padding of its own — the host grid cell already sits level with
    // the right panel; every gap counts.
    <div className="flex-1 w-full h-full flex flex-col min-h-0">
      {/* --> Top: Chart — expandable */}
      <motion.div
        initial={false}
        animate={{
          //! 50-11: collapsed card = 55% of the column (360px floor below), one 8px gap; expanded = 100%, no floor/gap.
          //! 50-12 (Verify F1): `height` is NEVER in the Framer payload on any tier — a value Framer once animated
          //! (55%) stayed as an inline style after a desktop→compact flip and overrode the dvh height. One authority:
          //! style.height below, eased by a CSS transition (percent↔percent tweens; dvh↔percent snaps).
          opacity: isMetricsExpanded ? 0 : 1,
          marginBottom: isMetricsExpanded || isChartExpanded ? 0 : 8,
          pointerEvents: isMetricsExpanded ? 'none' : 'auto',
        }}
        style={{
          minHeight: !isChartExpanded && !isMetricsExpanded ? collapsedSize.minHeight : 0,
          height: isMetricsExpanded ? 0 : isChartExpanded ? '100%' : collapsedSize.height,
          transition: 'height 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        // Layout/animation shell only — SmartChart renders its own DS card; a second
        // card here (border/rounded/shadow + hardcoded blue #1a1d24) double-enclosed it.
        className="w-full min-h-0 overflow-hidden relative shrink-0"
      >
        <div className="h-full w-full relative">
          {instrument ? (
            <SmartChart
              key={`chart-${deploymentId || strategyId}`}
              timeframe={activeTimeframe}
              rolloverDays={rolloverDays}
              isContinuous={isContinuous}
              layers={memoizedLayers}
              externalIndicators={strategyIndicators}
              id={strategyId}
              // 50-6 D35 — SmartChart's OWN nodes prop feeds useIndicators' computation
              // (functional key + /calculate deps); reads computationNodes, NOT the
              // display `nodes` (which may be the EFFECTIVE/merged set).
              nodes={computationNodes}
              edges={edges}
              chartType={activeChartType}
              availableTypes={null}
              onChartTypeChange={setActiveChartType}
              onTimeframeChange={setActiveTimeframe}
              heightClass="h-full"
              user={user}
              instrument={instrument}
              instruments={instruments}
              general={general}
              dateRange={dateRange}
              fetchDateRange={fetchDateRange}
              liveTick={liveTick}
                liveNodeResults={liveNodeResults}
                lastEvalTime={lastEvalTime}
                liveBarTimeframe={liveBarTimeframe}
              mode={mode}
              replayMode={replayMode}
              replayRunId={replayRunId}
              onBacktestRequired={onBacktestRequired}
              tradeMarkers={tradeMarkers}
              levelHistory={levelHistory}
              levelDisplayNodes={levelDisplayNodes}
              onCrosshairMove={handleCrosshairMove}
              onDataLoaded={handleDataLoaded}
              onIndicatorsUpdate={setIndicatorMaps}
              tickType={tickType}
              resolvedInstrumentId={resolvedInstrumentId}
              onGapRepair={onGapRepair}
              compactPanes={!isChartExpanded}
              paneStripMode={isPhone ? 'tabs' : 'rows'}
              onPaneActivate={handleToggleChartExpanded}
              canvasAction={(
                <button
                  onClick={handleToggleChartExpanded}
                  className="p-1 rounded-full bg-bg-3/80 dark:bg-bg-2/80 hover:bg-bg-3 text-fg-3 hover:text-fg-1 transition-colors shadow-sm border border-[var(--divider)]"
                  aria-label={isChartExpanded ? 'Collapse chart' : 'Expand chart'}
                >
                  {isChartExpanded ? <FiMinimize2 size={12} /> : <FiMaximize2 size={12} />}
                </button>
              )}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-fg-3 text-sm">
              Select an instrument to view chart
            </div>
          )}
        </div>
      </motion.div>

      {/* --> Bottom: R&D Watch Dashboard */}
      <motion.div
        initial={false}
        animate={{
          flex: isChartExpanded ? 0 : 1,
          opacity: isChartExpanded ? 0 : 1,
          pointerEvents: isChartExpanded ? 'none' : 'auto',
        }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
        className="w-full overflow-hidden flex flex-col"
        style={{ minHeight: isChartExpanded ? 0 : isCompact ? COMPACT_DASHBOARD_MIN_HEIGHT : undefined }}
      >
        <RDWatchDashboard
          {...dashboardProps}
          onPopOut={handlePopOut}
          onItemClick={handleDashboardItemClick}
          isMetricsExpanded={isMetricsExpanded}
          onToggleMetricsExpanded={handleToggleMetricsExpanded}
          responsiveLayout
        />
      </motion.div>

      {/* --> 50-14 panel Dashboard instance: SIBLING of the bottom motion wrapper above (never inside it), so the
          chart-expand collapse (flex 0 / opacity 0 / pointer-events none) cannot hide it. Same props object. */}
      {panelDashboardHost && createPortal(
        <RDWatchDashboard
          {...dashboardProps}
          panelMode
          onItemClick={onPanelItemClick || handleDashboardItemClick}
        />,
        panelDashboardHost,
      )}
    </div>
  );
};

export default StrategyAnalysisView;
